import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import SavedViews from '../components/SavedViews';
import {
  Users, UserCheck, AlertCircle, Ban, Clock, HelpCircle,
  Search, RefreshCw, X, ListChecks, UserPlus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'ready', label: 'Ready', icon: UserCheck, color: 'text-blue-700', testid: 'queue-card-ready', endpoint: 'ready' },
  { key: 'review', label: 'Review', icon: AlertCircle, color: 'text-amber-700', testid: 'queue-card-review', endpoint: 'review' },
  { key: 'rejected', label: 'Rejected', icon: Ban, color: 'text-red-700', testid: 'queue-card-rejected', endpoint: 'rejected' },
  { key: 'screening_pending', label: 'Screening Pending', icon: Clock, color: 'text-indigo-700', testid: 'queue-card-screening', endpoint: 'search' },
  { key: 'unknown', label: 'Unknown', icon: HelpCircle, color: 'text-gray-600', testid: 'queue-card-unknown', endpoint: 'search' },
];

export default function RecruiterQueuePage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState('ready');
  const [rows, setRows] = useState({ items: [], total: 0, page: 1, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState(null);

  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('');
  const [workflowF, setWorkflowF] = useState('');
  const [poolF, setPoolF] = useState('');

  const [assignRecruiters, setAssignRecruiters] = useState('');
  const [assignMsg, setAssignMsg] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignHidden, setAssignHidden] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (search) p.append('search', search);
    if (statusF) p.append('status', statusF);
    if (workflowF) p.append('workflow', workflowF);
    if (poolF) p.append('talent_pool', poolF);
    return p;
  }, [search, statusF, workflowF, poolF]);

  const fetchOverview = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/recruiter-queue/overview?${buildParams()}`, { headers });
      if (r.status === 403) throw new Error('This feature is currently disabled.');
      if (!r.ok) throw new Error('Failed to load recruiter queue.');
      setOverview(await r.json());
    } catch (e) { setError(e.message); }
  }, [token, buildParams]);

  const fetchQueue = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const p = buildParams();
      p.append('page', String(page));
      p.append('page_size', '25');
      const tabDef = TABS.find(t => t.key === tab);
      // screening_pending & unknown are served through /search with a workflow/derived filter
      let url;
      if (tab === 'screening_pending') {
        const sp = new URLSearchParams(p); sp.set('workflow', 'SCREENING_PENDING');
        url = `${API_URL}/api/recruiter-queue/search?${sp}`;
      } else if (tab === 'unknown') {
        url = `${API_URL}/api/recruiter-queue/search?${p}`;
      } else {
        url = `${API_URL}/api/recruiter-queue/${tabDef.endpoint}?${p}`;
      }
      const r = await fetch(url, { headers });
      if (r.status === 403) throw new Error('This feature is currently disabled.');
      if (!r.ok) throw new Error('Failed to load queue.');
      const data = await r.json();
      if (tab === 'unknown') data.items = (data.items || []).filter(i => i.queue === 'unknown');
      setRows(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token, buildParams, page, tab]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchQueue(); }, [fetchQueue]);
  useEffect(() => { setPage(1); }, [tab, search, statusF, workflowF, poolF]);

  const openPreview = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/recruiter-queue/application/${id}`, { headers });
      if (r.ok) setPreview(await r.json());
    } catch { /* preview is best-effort */ }
  };

  const distributeCurrentQueue = async () => {
    const recruiters = assignRecruiters.split(',').map((s) => s.trim()).filter(Boolean);
    const items = (rows.items || []);
    if (recruiters.length === 0) { setAssignMsg('Enter at least one recruiter email.'); return; }
    if (items.length === 0) { setAssignMsg('No applications in the current queue to distribute.'); return; }
    setAssignBusy(true); setAssignMsg('');
    let assigned = 0, failed = 0;
    for (let i = 0; i < items.length; i++) {
      const recruiter = recruiters[i % recruiters.length];
      try {
        const r = await fetch(`${API_URL}/api/recruiter-actions/assign`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ application_id: items[i].id, recruiter }),
        });
        if (r.ok) assigned += 1; else failed += 1;
      } catch { failed += 1; }
    }
    setAssignBusy(false);
    setAssignMsg(`Round-robin complete: ${assigned} assigned across ${recruiters.length} recruiter(s)${failed ? `, ${failed} failed` : ''}.`);
    fetchOverview(); fetchQueue();
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="recruiter-queue-page">
        <Breadcrumb items={[{ label: 'Recruiter Queue' }]} />

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ListChecks className="w-6 h-6 text-blue-700" /> Recruiter Queue
            </h1>
            <p className="text-sm text-gray-600">Read-only working queues built from persisted applicant metadata.</p>
          </div>
          <button onClick={() => { fetchOverview(); fetchQueue(); }} data-testid="queue-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-900 bg-blue-50 rounded-lg hover:bg-blue-100">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Overview cards / tabs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {TABS.map(({ key, label, icon: Icon, color, testid }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={testid}
              className={`text-left bg-white rounded-xl border p-4 transition ${tab === key ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${color}`} />
                <span className="text-2xl font-bold text-gray-900">{overview?.counts?.[key] ?? 0}</span>
              </div>
              <p className="text-xs text-gray-600">{label}</p>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
            <div className="flex-1 relative min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder="Search name, email, phone, CDL..." value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="queue-search"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="queue-filter-status"
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-w-[150px]">
              <option value="">All Statuses</option>
              {['new', 'reviewed', 'contacted', 'in_progress', 'approved', 'rejected'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={workflowF} onChange={(e) => setWorkflowF(e.target.value)} data-testid="queue-filter-workflow"
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-w-[150px]">
              <option value="">All Workflows</option>
              {['NOT_ROUTED', 'READY_FOR_RECRUITER', 'MANUAL_REVIEW', 'AUTO_REJECTED', 'SCREENING_PENDING'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={poolF} onChange={(e) => setPoolF(e.target.value)} data-testid="queue-filter-pool"
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-w-[150px]">
              <option value="">All Pools</option>
              {['NONE', 'ACTIVE_POOL', 'REVIEW_POOL', 'FUTURE_POOL', 'REJECTED_POOL', 'NOT_ELIGIBLE'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {!assignHidden && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6" data-testid="queue-assign-bar">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 min-w-[180px]">
                <UserPlus className="w-4 h-4 text-blue-700" /> Distribute Current Queue
              </div>
              <input type="text" value={assignRecruiters} onChange={(e) => setAssignRecruiters(e.target.value)}
                placeholder="Recruiter emails (comma-separated)" data-testid="queue-assign-recruiters"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-w-[220px]" />
              <button onClick={distributeCurrentQueue} disabled={assignBusy} data-testid="queue-assign-distribute"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 disabled:opacity-50">
                {assignBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Round-Robin Assign
              </button>
            </div>
            {assignMsg && <p className="text-xs text-gray-600 mt-2" data-testid="queue-assign-msg">{assignMsg}</p>}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6" data-testid="queue-error">
            <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5" /> {error}</div>
          </div>
        )}

        {/* Queue table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="queue-table">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 capitalize">{tab.replace('_', ' ')} Queue ({rows.total})</h3>
          </div>
          {loading ? (
            <div className="py-16 flex items-center justify-center text-gray-500" data-testid="queue-loading">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading...
            </div>
          ) : rows.items.length === 0 ? (
            <div className="py-16 text-center text-gray-400" data-testid="queue-empty">No applications in this queue.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    {['Name', 'Email', 'Workflow', 'Pool', 'Screening', 'Submitted', ''].map(h => (
                      <th key={h} className="px-4 py-2 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.items.map(a => (
                    <tr key={a.id} data-testid={`queue-row-${a.id}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-900">{a.first_name} {a.last_name}</td>
                      <td className="px-4 py-2 text-gray-600">{a.email}</td>
                      <td className="px-4 py-2 text-gray-600">{a.workflow_status}</td>
                      <td className="px-4 py-2 text-gray-600">{a.talent_pool}</td>
                      <td className="px-4 py-2 text-gray-600">{a.screening_status}</td>
                      <td className="px-4 py-2 text-gray-500">{(a.created_at || '').slice(0, 10)}</td>
                      <td className="px-4 py-2">
                        <button onClick={() => openPreview(a.id)} data-testid={`queue-preview-btn-${a.id}`}
                          className="text-blue-700 hover:underline text-xs font-medium">Preview</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rows.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="queue-prev-page"
                className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 disabled:opacity-50">Previous</button>
              <span className="text-sm text-gray-600">Page {rows.page} of {rows.total_pages}</span>
              <button disabled={page >= rows.total_pages} onClick={() => setPage(p => p + 1)} data-testid="queue-next-page"
                className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 disabled:opacity-50">Next</button>
            </div>
          )}
        </div>

        {/* Preview drawer */}
        {preview && (
          <div className="fixed inset-0 z-50 flex justify-end" data-testid="queue-preview">
            <div className="absolute inset-0 bg-black/30" onClick={() => setPreview(null)} />
            <div className="relative bg-white w-full max-w-md h-full shadow-xl p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Application Preview</h3>
                <button onClick={() => setPreview(null)} data-testid="queue-preview-close" className="text-gray-500 hover:text-gray-800"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3 text-sm">
                {[
                  ['Name', `${preview.first_name} ${preview.last_name}`],
                  ['Email', preview.email], ['Phone', preview.phone],
                  ['Country', preview.country], ['CDL', `${preview.cdl_class} / ${preview.cdl_state}`],
                  ['Status', preview.status], ['Eligibility', preview.eligibility_status],
                  ['Screening', preview.screening_status], ['Workflow', preview.workflow_status],
                  ['Workflow Route', preview.workflow_route], ['Talent Pool', preview.talent_pool],
                  ['Queue', preview.queue], ['Submitted', (preview.created_at || '').slice(0, 10)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-500">{k}</span>
                    <span className="text-gray-900 font-medium text-right">{v || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
